import './nav-desktop.scss';
import './nav-mobile.scss';
import {computePosition, offset, shift} from '@floating-ui/dom';
import throttle from 'just-throttle';
import debounce from 'just-debounce-it';
const navButtons = document.querySelectorAll(
  '.navContainer .menu-item-has-children > a',
);
const subMenus = document.querySelectorAll('.subMenuWrapper');
const separator = document.querySelector('.separator');
const navWrapper = document.querySelector('.navWrapper');
const cart = document.querySelector('.cart');
const cartQuantity = document.querySelector('.cartItems');
const mobileContainer = document.querySelector(
  '.mobileContainer',
) as HTMLElement;
const mobileOverlay = document.querySelector('.mobileOverlay') as HTMLElement;
const burgerWrapper = document.querySelector(
  'div.burgerWrapper',
) as HTMLElement;
const hamburgerButton = document.querySelector<HTMLButtonElement>('.buttonToggle');
const mobileNavButtons = document.querySelectorAll('.menu-item-has-children');
const body = document.querySelector('body') as HTMLElement;
const isTouch = window.matchMedia('(hover: none)').matches;
const subMenuLinks = document.querySelectorAll(
  '.sub-menu a',
// eslint-disable-next-line no-undef
) as NodeListOf<HTMLAnchorElement>;

function update() {
  navButtons.forEach((buttonNav) => {
    const subMenu = buttonNav.nextElementSibling as HTMLElement;

    computePosition(buttonNav, subMenu, {
      placement: 'bottom-start',
      middleware: [
        offset({crossAxis: -16}),
        shift({
          padding: 10,
        }),
      ],
    }).then(({x, y}) => {
      Object.assign(subMenu.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  });
}

function expandMobileSubmenu(e: Event) {
  const navButton = e.target as HTMLElement;
  const currentSubMenu = navButton.nextElementSibling
    ?.firstElementChild as HTMLElement;
  if (!navButton) return;
  const mobileContainer = document.querySelector(
    '.mobileContainer',
  ) as HTMLElement;
  const menuHeight = currentSubMenu.clientHeight;
  // Scale border timing to menu height: taller = longer delay/duration (avoids double border + slow feel on short menus)
  const borderDelay = 0.2 + Math.min(menuHeight / 1200, 0.3);
  const borderDuration = 0.15 + Math.min(menuHeight / 1000, 0.3);
  mobileContainer.style.setProperty('--border-close-delay', `${borderDelay.toFixed(2)}s`);
  mobileContainer.style.setProperty('--border-duration', `${borderDuration.toFixed(2)}s`);

  // Close any open submenus
  const subMenus = document.querySelectorAll(
    '.mobileContainer .sub-menu',
    // eslint-disable-next-line no-undef
  ) as NodeListOf<HTMLElement>;

  subMenus.forEach((subMenu) => {
    const subMenuLi = subMenu?.parentElement?.parentElement as HTMLElement;
    if (subMenu === currentSubMenu) {
      subMenuLi.classList.toggle('menuActive');
    } else {
      subMenuLi.classList.remove('menuActive');
    }
  });
}

function submenuTriggerEl(subMenu: HTMLElement): HTMLElement | null {
  const prev = subMenu.previousElementSibling;
  return prev instanceof HTMLElement ? prev : null;
}

function showSubmenu(e: Event) {
  e.preventDefault();
  const navButton = (e.currentTarget ?? e.target) as HTMLElement;
  if (!navButton) return;
  const subMenu = navButton.nextElementSibling as HTMLElement;
  setShowSubmenu(subMenu);
}

function hideSubmenu(e: Event) {
  const navButton = (e.currentTarget ?? e.target) as HTMLElement;
  const subMenu = navButton.nextElementSibling as HTMLElement;

  if (isMouseEvent(e)) {
    const rt = e.relatedTarget as Node | null;
    if (rt && subMenu.contains(rt)) return;
  }
  if (!navButton) return;

  setHideSubmenu(subMenu);
}

function isMouseEvent(event: Event): event is MouseEvent {
  return event instanceof MouseEvent;
}

function setHideSubmenu(subMenu: HTMLElement) {
  subMenu.style.display = '';
  submenuTriggerEl(subMenu)?.setAttribute('aria-expanded', 'false');
  update();
}

function setShowSubmenu(subMenu: HTMLElement) {
  submenuTriggerEl(subMenu)?.setAttribute('aria-expanded', 'true');
  subMenu.setAttribute('role', 'menu');
  subMenu.style.display = 'block';
  update();
}
/**
 * These events only get added at pageload.
 * If you use Chrome devtools and switch between regular and touch mode you
 * will need to reload the page so the correct event listeners are added.
 */
if (!isTouch) {
  navButtons.forEach((buttonNav) => {
    buttonNav.addEventListener('mouseenter', (e) => showSubmenu(e));
    buttonNav.addEventListener('mouseleave', (e) => hideSubmenu(e));
  });
} else {
  navButtons.forEach((buttonNav) => {
    buttonNav.addEventListener('touchend', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const trigger = e.currentTarget as HTMLElement;
      trigger.dataset.active = trigger.dataset.active || 'false'; // initialize to false if there is no value
      trigger.dataset.active =
        trigger.dataset.active === 'true' ? 'false' : 'true';

      if (trigger.dataset.active === 'true') {
        showSubmenu(e);
      } else {
        hideSubmenu(e);
      }
    });
  });
}

function hideSubList(e: Event) {
  const subList = (e.currentTarget ?? e.target) as HTMLElement;
  if (!subList) return;
  if (!isMouseEvent(e)) return;
  const related = e.relatedTarget as Node | null;
  const item = subList.closest('li.menu-item-has-children');
  if (related && item?.contains(related)) return;
  setHideSubmenu(subList);
}

subMenus.forEach((subMenu) => {
  subMenu.addEventListener('mouseleave', (e) => hideSubList(e));
});

let prevScrollPosition: number;

function detectScrollEvent(): void {
  const currentScrollPosition: number = window.scrollY;
  const scrollDirection: string =
    currentScrollPosition > prevScrollPosition ? 'down' : 'up';

  if (scrollDirection === 'down' && currentScrollPosition > 100) {
    navWrapper?.classList.add('stickyHide');
  } else {
    navWrapper?.classList.remove('stickyHide');
  }

  prevScrollPosition = currentScrollPosition;

  if (currentScrollPosition === 0) {
    separator?.classList.remove('scroll');
  }
  if (currentScrollPosition !== 0 && !separator?.classList.contains('scroll')) {
    separator?.classList.add('scroll');
  }
}

window.addEventListener('scroll', throttle(detectScrollEvent, 100), {
  passive: true,
});

window.addEventListener('scroll', debounce(detectScrollEvent, 600), {
  passive: true,
});

function getHeaderBottom() {
  const headerRevamp = document.querySelector(
    '#headerRevampWrapper',
  ) as HTMLElement;
  const headerRevampBounding = headerRevamp?.getBoundingClientRect();
  headerRevamp.style.setProperty(
    '--header-revamp-bottom',
    `${headerRevampBounding?.bottom}px`,
  );
}

window.addEventListener('scroll', debounce(getHeaderBottom, 600));
// This may need to go into DOMContentLaoded event listener when experiment is done
getHeaderBottom();

function wooCommerceCartQuantity() {
  const wooCommerceCookie = 'woocommerce_cart_quantity=';

  const cartQtyCookie = document.cookie
    .split(';')
    .find((cookie) => cookie.trim().startsWith(wooCommerceCookie));
  let qty: number = 0;
  if (cartQtyCookie) {
    qty = Number(cartQtyCookie.split('=')[1]);
  }
  if (cartQuantity) {
    cartQuantity.innerHTML = qty >= 1 ? qty.toString() : '';
  }

  cart?.setAttribute(
    'aria-label',
    `${qty} item${qty !== 1 ? 's' : ''} in cart`,
  );
}

wooCommerceCartQuantity();

const BURGER_TRANSITION =
  'transform 0.2s cubic-bezier(0.76, 0, 0.24, 1)' as const;

function setHamburgerVisualState(button: HTMLButtonElement, isActive: boolean) {
  const delays = isActive
    ? ['0s', '0.1s', '0.2s', '0.3s', '0.35s']
    : ['0.4s', '0.3s', '0.2s', '0.1s', '0.1s'];
  const barSelectors = ['.burgerBar1', '.burgerBar2', '.burgerBar3', '.burgerBar4', '.burgerBar5'];
  barSelectors.forEach((sel, i) => {
    const bar = button.querySelector<HTMLElement>(sel);
    if (bar) bar.style.transition = `${BURGER_TRANSITION} ${delays[i]}`;
  });
  button.classList.toggle('is-active', isActive);
}

function handleToggleHamburger(e: Event) {
  const target = e.target as Node | null;
  if (!hamburgerButton || !target || !hamburgerButton.contains(target)) return;

  const isActive = burgerWrapper.classList.toggle('active');

  setHamburgerVisualState(hamburgerButton, isActive);
  mobileContainer?.classList.toggle('activateDrawer', isActive);
  mobileOverlay?.classList.toggle('mobileOverlayVisible', isActive);
  isActive
    ? body.setAttribute('style', 'overflow: hidden')
    : (body.style.overflow = '');
}

function closeMobileContainer() {
  if (
    burgerWrapper.classList.contains('active') &&
    body.style.overflow === 'hidden'
  ) {
    burgerWrapper.classList.remove('active');
    if (hamburgerButton) setHamburgerVisualState(hamburgerButton, false);
    mobileContainer?.classList.remove('activateDrawer');
    mobileOverlay?.classList.remove('mobileOverlayVisible');
    body.style.overflow = '';
  }
}

if (hamburgerButton) {
  hamburgerButton.addEventListener('click', handleToggleHamburger);
}

document.addEventListener('click', (e) => {
  const target = e.target as Node | null;
  if (
    !burgerWrapper.contains(target) &&
    e.target !== hamburgerButton &&
    burgerWrapper.classList.contains('active') &&
    !mobileContainer.contains(target)
  ) {
    closeMobileContainer();
  }
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    closeMobileContainer();
  }
});

mobileNavButtons.forEach((buttonNav) => {
  buttonNav.addEventListener('click', (e) => expandMobileSubmenu(e));
});

// this is for using keyboard to navigate the menu
navButtons.forEach((buttonNav) => {
  buttonNav.addEventListener('focus', (e) => showSubmenu(e));
  const subMenu = buttonNav.nextElementSibling as HTMLElement;
  const subMenuItems = subMenu.querySelectorAll('a');
  buttonNav.addEventListener('blur', (e) => {
    const focusEvent = e as FocusEvent;
    const relatedTarget = focusEvent.relatedTarget as HTMLElement;
    if (!subMenu.contains(relatedTarget)) {
      setHideSubmenu(subMenu);
    }
  });
  subMenuItems.forEach((subMenuItem) => {
    subMenuItem.addEventListener('blur', (e) => {
      const relatedTarget = e.relatedTarget as HTMLElement;
      if (!subMenu.contains(relatedTarget)) {
        setHideSubmenu(subMenu);
      }
    });
  });
});

document.body.addEventListener('product-qty-change', () => {
  wooCommerceCartQuantity();
});

// loop thru all the submenus and look for a link with a phone number and remove it between 7pm and 5am pacific time
subMenuLinks.forEach((link) => {
  const phoneNumberRegex = /^(\+?1\s?)?(\(?\d{3}\)?[\s.-]?)?\d{3}[\s.-]?\d{4}$/;

  const currentTimeInPacific = new Date().toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
  });
  const currentHour = new Date(currentTimeInPacific).getHours();
  // Define restricted hours
  const hideStart = 19; // 7 pm is 19 in 24 hour time
  const hideEnd = 5; // 5 am

  const isOffHours = currentHour >= hideStart || currentHour < hideEnd;

  const phoneNumberToHide = link.innerText
    ? phoneNumberRegex.test(link.innerText)
    : false;

  if (isOffHours && phoneNumberToHide) {
    link.parentElement?.remove();
  }

  return link;
});

navButtons.forEach((link) => {
  const text = link.textContent;
  if (!text) return;
  const span = document.createElement('span');
  span.textContent = text;
  link.textContent = '';
  link.appendChild(span);
});